/* eslint-disable eqeqeq */
import { memo, useState } from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import moment from 'moment';

const STATUS_ACTIVE = 1;
const STATUS_COMPLETE = 2;

const statusChip = (status, t) => {
  if (status === STATUS_ACTIVE) {
    return (
      <Chip
        label={t('tasks.staffTask.status.active')}
        size='small'
        color='success'
        variant='outlined'
      />
    );
  }
  if (status === STATUS_COMPLETE) {
    return (
      <Chip
        label={t('tasks.staffTask.status.complete')}
        size='small'
        color='primary'
        variant='outlined'
      />
    );
  }
  return (
    <Chip
      label={t('tasks.staffTask.status.inactive')}
      size='small'
      color='default'
      variant='outlined'
    />
  );
};

const isImage = (file) =>
  /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(file || '');

const ShowroomTasksModal = ({ open, setOpen, tasks = [], contract }) => {
  const { t } = useTranslation();
  const [previewImg, setPreviewImg] = useState(null);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth='md'
      fullWidth
      PaperProps={{ className: '!base-border !shadow-none' }}
    >
      <DialogTitle className='!bg-gray-100 flex items-center justify-between'>
        <div className='flex flex-col'>
          <span className='font-medium'>
            {t('showroom.homeDetails.tasksModal.title')}
          </span>
          {contract?.name && (
            <span className='text-xs text-gray-500 font-normal'>
              {t('shaxmatka.homeDetail.contractNumber')}: №{contract.name}
            </span>
          )}
        </div>
        <IconButton size='small' onClick={() => setOpen(false)}>
          <i className='bi bi-x text-xl' />
        </IconButton>
      </DialogTitle>

      <DialogContent className='!p-4'>
        {tasks?.length ? (
          <div className='flex flex-col gap-3'>
            {tasks.map((task) => (
              <div
                key={task.id}
                className='flex flex-col gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3'
              >
                {/* Sarlavha + status */}
                <div className='flex items-start justify-between gap-3 flex-wrap'>
                  <span className='font-medium text-sm text-gray-800'>
                    {task.title || `#${task.id}`}
                  </span>
                  {statusChip(task.status, t)}
                </div>

                {/* Meta: mas'ul xodim + sanalar */}
                <div className='flex items-center gap-x-4 gap-y-1 text-xs text-gray-500 flex-wrap'>
                  {task.user?.name && (
                    <span>
                      <i className='bi bi-person mr-1' />
                      {task.user.name}
                    </span>
                  )}
                  {task.date && (
                    <span>
                      <i className='bi bi-calendar mr-1' />
                      {moment(task.date).format('DD.MM.YYYY')}
                    </span>
                  )}
                  {task.till_date && (
                    <span
                      className={
                        task.status === STATUS_ACTIVE &&
                        moment(task.till_date).isBefore(moment(), 'day')
                          ? 'text-red-500 font-medium'
                          : ''
                      }
                    >
                      <i className='bi bi-calendar-x mr-1' />
                      {moment(task.till_date).format('DD.MM.YYYY')}
                    </span>
                  )}
                  {task.date_complete && (
                    <span className='text-green-600'>
                      <i className='bi bi-check-circle mr-1' />
                      {moment(task.date_complete).format('DD.MM.YYYY')}
                    </span>
                  )}
                </div>

                {/* Rasmlar va fayllar */}
                {task.files?.length > 0 && (
                  <div className='mt-1 flex flex-col gap-1 border-t border-gray-100 pt-2'>
                    <span className='text-xs font-medium text-gray-400 uppercase tracking-wide'>
                      <i className='bi bi-paperclip mr-1' />
                      {t('tasks.staffTask.fileModal.title')} ({task.files.length})
                    </span>

                    {/* Rasmlar - galereya */}
                    {task.files.some((f) => isImage(f.file)) && (
                      <div className='flex flex-row gap-2 flex-wrap'>
                        {task.files
                          .filter((f) => isImage(f.file))
                          .map((file) => {
                            const fileUrl = `${process.env.REACT_APP_BACKEND_URL}/${file.file}`;
                            return (
                              <button
                                key={file.id}
                                type='button'
                                onClick={() => setPreviewImg(fileUrl)}
                                className='flex flex-col gap-1 items-center'
                              >
                                <img
                                  src={fileUrl}
                                  alt={file.title || ''}
                                  className='h-20 w-20 rounded-lg object-cover border border-gray-200 hover:opacity-80'
                                />
                                {file.title && (
                                  <span className='text-[10px] text-gray-500 truncate max-w-[80px]'>
                                    {file.title}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                      </div>
                    )}

                    {/* Boshqa fayllar - ro'yxat */}
                    {task.files
                      .filter((f) => !isImage(f.file))
                      .map((file) => {
                        const fileUrl = file.file
                          ? `${process.env.REACT_APP_BACKEND_URL}/${file.file}`
                          : null;
                        return (
                          <div
                            key={file.id}
                            className='flex items-center gap-2 text-xs bg-gray-50 rounded-lg px-3 py-2'
                          >
                            <i className='bi bi-file-earmark text-gray-400 text-sm shrink-0' />
                            <span className='font-medium text-gray-700 truncate flex-1'>
                              {file.title || file.file?.split('/').pop()}
                            </span>
                            <span className='text-gray-600 whitespace-nowrap'>
                              <i className='bi bi-person mr-1' />
                              {file.user?.name || '—'}
                            </span>
                            {file.date && (
                              <span className='text-gray-500 whitespace-nowrap'>
                                {moment(file.date).format('DD.MM.YYYY')}
                              </span>
                            )}
                            {fileUrl && (
                              <a
                                href={fileUrl}
                                target='_blank'
                                rel='noreferrer'
                                className='text-blue-500 hover:text-blue-700 shrink-0'
                              >
                                <i className='bi bi-download text-sm' />
                              </a>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}

                {/* Izohlar */}
                {task.comments?.length > 0 && (
                  <div className='mt-1 flex flex-col gap-1 border-t border-gray-100 pt-2'>
                    <span className='text-xs font-medium text-gray-400 uppercase tracking-wide'>
                      <i className='bi bi-chat-left-text mr-1' />
                      {t('tasks.staffTask.commentModal.title')} ({task.comments.length})
                    </span>
                    {task.comments.map((comment) => (
                      <div
                        key={comment.id}
                        className='flex gap-2 text-xs bg-gray-50 rounded-lg px-3 py-2'
                      >
                        <div className='flex flex-col flex-1 gap-0.5'>
                          <div className='flex items-center gap-2 text-gray-500'>
                            <span className='font-medium text-gray-700'>
                              <i className='bi bi-person mr-1' />
                              {comment.user?.name || '—'}
                            </span>
                            {comment.date && (
                              <span>
                                {moment(comment.date).format('DD.MM.YYYY')}
                              </span>
                            )}
                          </div>
                          <p className='text-gray-700 whitespace-pre-wrap'>
                            {comment.text}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className='flex items-center justify-center py-8 text-gray-400'>
            <i className='bi bi-exclamation-octagon text-xl mr-2' />
            {t('common.global.noDataFound')}
          </div>
        )}

        {contract?.id && (
          <div className='mt-4 flex justify-end'>
            <Link
              to={`/admin/contract/view/${contract.id}`}
              className='no-underline'
            >
              <Button variant='contained' color='primary' size='small'>
                {t('contract.view.title')} №{contract?.name}
              </Button>
            </Link>
          </div>
        )}

        {/* Rasm preview */}
        <Dialog
          open={!!previewImg}
          onClose={() => setPreviewImg(null)}
          maxWidth='lg'
          PaperProps={{
            className: '!bg-transparent !shadow-none',
            style: { margin: 8 },
          }}
        >
          <div className='relative'>
            <IconButton
              size='small'
              onClick={() => setPreviewImg(null)}
              className='!absolute !top-2 !right-2 !bg-black/50 !text-white hover:!bg-black/70'
            >
              <i className='bi bi-x text-xl' />
            </IconButton>
            <img
              src={previewImg}
              alt=''
              className='max-w-[90vw] max-h-[90vh] rounded-xl object-contain'
            />
          </div>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default memo(ShowroomTasksModal);
